import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { BOOKING_SETTINGS_ROUTE_FEATURES } from './pages/booking/settings/bookingSettingsTabs';
import { useTranslation } from '@/store/languageStore';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

import { ThemeProvider } from './components/Theme/ThemeProvider';
import { DynamicThemeApplicator } from './components/Theme/DynamicThemeApplicator';
import Layout from './components/layout/Layout';
import ProtectedRoute from './guards/ProtectedRoute';
import AdminRoute from './guards/AdminRoute';
import OldBookingRoute from './guards/OldBookingRoute';
import ServiceCostsReportGate from './guards/ServiceCostsReportGate';
import Dashboard from './pages/dashboard/Dashboard';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import Landing from './pages/auth/Landing';
import SamlError from './pages/auth/SamlError';
import SsoLogin from './pages/auth/SsoLogin';
import SsoComplete from './pages/auth/SsoComplete';
import EmailVerification from './pages/auth/EmailVerification';
import ResetPassword from './pages/auth/ResetPassword';
import FleetOverview from './pages/fleet/FleetOverview';
import UnplugEventsPage from './pages/fleet/UnplugEventsPage';
import FleetDashboard from './pages/fleet/FleetDashboard';
import EconomyExportPage from './pages/fleet/EconomyExportPage';
import Reports from './pages/reports/Reports';
import OrganizationDashboard from './pages/organization/OrganizationDashboard';
import CrmDashboard from './pages/crm/CrmDashboard';
import TripsView from './pages/reports/TripsView';
import SpeedComplianceReport from './pages/reports/SpeedComplianceReport';
import EnergyConsumptionReport from './pages/reports/EnergyConsumptionReport';
import VehicleWearReport from './pages/reports/VehicleWearReport';
import UnderutilizedReport from './pages/reports/UnderutilizedReport';
import CO2Report from './pages/reports/CO2Report';
import BookingStatsReport from './pages/reports/BookingStatsReport';
import GeofenceZoneReport from './pages/reports/GeofenceZoneReport';
import VehicleKpiReport from './pages/reports/VehicleKpiReport';
import DrivingLog from './pages/reports/DrivingLog';
import OdometerReport from './pages/reports/OdometerReport';
import HeatmapImport from './pages/analysis/HeatmapImport';
import StopTimeAnalysis from './pages/analysis/StopTimeAnalysis';
import RolesManagement from './pages/admin/RolesManagement';
import OrganizationsManagement from './pages/admin/OrganizationsManagement';
import UsersManagement from './pages/admin/UsersManagement';
import SysAdmin from './pages/admin/SysAdmin';
import DataMigration from './pages/admin/DataMigration';
import TranslationsManagement from './pages/admin/TranslationsManagement';
import DevicesManagement from './pages/admin/DevicesManagement';
import DeviceManagement from './pages/admin/DeviceManagement';
import DeviceAvlCapabilities from './pages/admin/DeviceAvlCapabilities';
import ObdVehicleListPage from './pages/admin/ObdVehicleListPage';
import ObdKnowledgePage from './pages/admin/ObdKnowledgePage';
import SimCardManagement from './pages/admin/SimCardManagement';
import GeofenceEvents from './pages/admin/GeofenceEvents';
import DeviceCommandBuilder from './pages/admin/DeviceCommandBuilder';
import FotaWebMigration from './pages/admin/FotaWebMigration';
import ThemeSettingsPage from './pages/admin/ThemeSettingsPage';
import TripAnalysisPage from './pages/admin/TripAnalysisPage';
import AuditLog from './pages/admin/AuditLog';
import GeneralAuditLog from './pages/admin/GeneralAuditLog';
import ErrorLog from './pages/admin/ErrorLog';
import CarInfoFilterConfig from './pages/admin/CarInfoFilterConfig';
import EcoSettings from './pages/admin/EcoSettings';
import SlowQueryMonitor from './pages/sysadmin/SlowQueryMonitor';
import CertificateMonitor from './pages/sysadmin/CertificateMonitor';
import SecurityPage from './pages/sysadmin/SecurityPage';
import SsoLogPage from './pages/sysadmin/SsoLogPage';
import GpsDataLagPage from './pages/sysadmin/GpsDataLagPage';
import DeviceQueueMonitor from './pages/sysadmin/DeviceQueueMonitor';
import DataReplay from './pages/sysadmin/DataReplay';
import ImportSourcesManagement from './pages/sysadmin/ImportSourcesManagement';
import ImportsManagement from './pages/admin/imports';
import PackagesManagement from './pages/admin/PackagesManagement';
import UserManual from './pages/UserManual';
import TestRunner from './pages/testing/TestRunner';
import OrganizationChart from './pages/organization/OrganizationChart';
import CustomerList from './pages/crm/CustomerList';
import CustomerWizard from './pages/crm/CustomerWizard';
import ComingSoon from './pages/crm/ComingSoon';
import SalesPipelinePage from './pages/crm/pipeline/SalesPipelinePage';
import ModuleCatalogPage from './pages/crm/ModuleCatalogPage';
import PipelineCaseFormPage from './pages/crm/pipeline/PipelineCaseFormPage';
import PipelineCaseDetailPage from './pages/crm/pipeline/PipelineCaseDetailPage';
import Pricelist from './pages/crm/Pricelist';
import ScbTesting from './pages/crm/ScbTesting';
import CrmProjectsPage from './pages/crm/Projects/CrmProjectsPage';
import CrmProjectDetailPage from './pages/crm/Projects/CrmProjectDetailPage';
import DevProjectsPage from './pages/admin/DevProjects/DevProjectsPage';
import DevProjectDetailPage from './pages/admin/DevProjects/DevProjectDetailPage';
import SystemLandingPage from './pages/system/SystemLandingPage';
import MapServicesDashboard from './pages/map/MapServicesDashboard';
import AILog from './pages/admin/AILog';
import FlinkeyBoxesPage from './pages/admin/FlinkeyBoxesPage';
import GuideManagement from './pages/admin/GuideManagement';
import { DeviceProfileProvider } from './context/DeviceProfileContext';

// Lazy-loaded pages (mindre initial bundle, snabbare first load)
const LiveTracking = lazy(() => import('./pages/tracking/liveTracking'));
const RouteAnalysis = lazy(() => import('./pages/analysis/RouteAnalysis').then((m) => ({ default: m.RouteAnalysis })));
const TripValidation = lazy(() => import('./pages/sysadmin/TripValidation'));
const TripSyncPage = lazy(() => import('./pages/sysadmin/TripSyncPage'));
const VehicleCarInfoSync = lazy(() => import('./pages/sysadmin/VehicleCarInfoSync'));
const EcoDriving = lazy(() => import('./pages/reports/EcoDriving'));
const RentalReport = lazy(() => import('./pages/reports/RentalReport'));
const DtcReport = lazy(() => import('./pages/reports/DtcReport'));
const ServiceCostsReport = lazy(() => import('./pages/reports/ServiceCostsReport'));
const FuelAnalysisReport = lazy(() => import('./pages/reports/FuelAnalysisReport'));
const FleetTcoReport = lazy(() => import('./pages/reports/FleetTcoReport'));
const ExecutiveDashboard = lazy(() => import('./pages/reports/ExecutiveDashboard'));
const TrendAnalysis = lazy(() => import('./pages/analysis/TrendAnalysis'));
const BenchmarkAnalysis = lazy(() => import('./pages/analysis/BenchmarkAnalysis'));
const Heatmap = lazy(() => import('./pages/analysis/Heatmap'));
const Geofences = lazy(() => import('./pages/admin/Geofences'));
const JamDetection = lazy(() => import('./pages/admin/JamDetection'));
const TrackPlayback = lazy(() => import('./pages/deque/TrackPlayback'));
const FuelManagement = lazy(() => import('./pages/deque/FuelManagement'));
const FuelCards = lazy(() => import('./pages/deque/FuelCards'));
const Assignments = lazy(() => import('./pages/deque/Assignments'));
const VehicleLog = lazy(() => import('./pages/deque/VehicleLog'));
const VehicleCategories = lazy(() => import('./pages/deque/VehicleCategories'));
const AlternativeFuels = lazy(() => import('./pages/deque/AlternativeFuels'));
const Charging = lazy(() => import('./pages/deque/Charging'));
const VehicleUsageStatuses = lazy(() => import('./pages/deque/VehicleUsageStatuses'));
const NordeaLeasingImport = lazy(() => import('./pages/deque/NordeaLeasingImport'));
const ProtectorInsuranceImport = lazy(() => import('./pages/deque/ProtectorInsuranceImport'));
const CostInvoiceImportPage = lazy(() => import('./pages/fleet/CostInvoiceImportPage'));
const VehicleCostAnalysisPage = lazy(() => import('./pages/fleet/VehicleCostAnalysisPage'));
const ReplacementChainPage = lazy(() => import('./pages/deque/ReplacementChainPage'));
const AIAnalysis = lazy(() => import('./pages/analysis/AIAnalysis'));
const CrashDetection = lazy(() => import('./pages/analysis/CrashDetection'));
const TireAnalysis = lazy(() => import('./pages/analysis/TireAnalysis'));
const SupportAdmin = lazy(() => import('./pages/admin/SupportAdmin'));
const DamageReports = lazy(() => import('./pages/admin/DamageReports'));
const SystemMonitor = lazy(() => import('./pages/sysadmin/SystemMonitor'));
const CustomerDetail = lazy(() => import('./pages/crm/CustomerDetail'));
const CraftsmanPage = lazy(() => import('./pages/workOrders/CraftsmanPage').then((m) => ({ default: m.CraftsmanPage })));
const DeliveryPage = lazy(() => import('./pages/workOrders/DeliveryPage').then((m) => ({ default: m.DeliveryPage })));
const HomecarePage = lazy(() => import('./pages/workOrders/HomecarePage').then((m) => ({ default: m.HomecarePage })));
const ServiceAgreementsPage = lazy(() => import('./pages/workOrders/ServiceAgreementsPage').then((m) => ({ default: m.ServiceAgreementsPage })));
const ServiceOverview = lazy(() => import('./pages/service/ServiceOverview'));
const PickupLocationsPage = lazy(() => import('./pages/booking/PickupLocationsPage'));
const SuppliersPage = lazy(() => import('./pages/booking/SuppliersPage'));
const RentalManagementPage = lazy(() => import('./pages/booking/RentalManagementPage'));
const BookingSettingsPage = lazy(() => import('./pages/booking/settings/BookingSettingsPage'));
const BookingCalendarPage = lazy(() => import('./pages/booking/bookingCalendar'));
const BookingRequestsPage = lazy(() => import('./pages/booking/BookingRequestsPage'));
const BookingBasisPage = lazy(() => import('./pages/booking/BookingBasisPage'));
const BookingSearchPage = lazy(() => import('./pages/booking/BookingSearchPage'));
const BookingOptimizationPage = lazy(() => import('./pages/booking/BookingOptimizationPage'));
const BookingCancelPage = lazy(() => import('./pages/booking/BookingCancelPage'));
const ResourceManagerPage = lazy(() => import('./pages/booking/ResourceManagerPage'));
const OldBookingPage = lazy(() => import('./pages/booking/OldBookingPage'));
const CollisionBookingsPage = lazy(() => import('./pages/booking/CollisionBookingsPage'));
const VehicleReplacementPage = lazy(() => import('./pages/booking/VehicleReplacementPage'));
const TravelPlannerPage = lazy(() => import('./pages/booking/TravelPlanner'));
const OwnCarTripFormPage = lazy(() => import('./pages/booking/ownCarTrip/OwnCarTripFormPage'));
const OwnCarTripsPage = lazy(() => import('./pages/booking/ownCarTrip/OwnCarTripsPage'));
const VehiclePoolPage = lazy(() => import('./pages/booking/VehiclePoolPage'));
const BookingPoolWatchPage = lazy(() => import('./pages/booking/BookingPoolWatchPage'));
const VehicleEquipmentTagsPage = lazy(() => import('./pages/booking/VehicleEquipmentTagsPage'));
const BookingResourcesPage = lazy(() => import('./pages/booking/BookingResourcesPage'));
const PoolCarDemo = lazy(() => import('./pages/demo/PoolCarDemo'));
const TidbokPage = lazy(() => import('./pages/tidbok/TidbokPage'));
const KeyManagement = lazy(() => import('./pages/system/KeyManagement'));
const TemperatureMonitoringOverview = lazy(() => import('./pages/temperature-monitoring/Overview'));
const TemperatureMonitoringSensors = lazy(() => import('./pages/temperature-monitoring/Sensors'));
const TemperatureMonitoringConfig = lazy(() => import('./pages/temperature-monitoring/Config'));
const TemperatureMonitoringRecipients = lazy(() => import('./pages/temperature-monitoring/Recipients'));
const TemperatureMonitoringSchedules = lazy(() => import('./pages/temperature-monitoring/Schedules'));
const TemperatureMonitoringReports = lazy(() => import('./pages/temperature-monitoring/Reports'));
const CarinfoOrgSync = lazy(() => import('./pages/admin/CarinfoOrgSync'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-[200px] text-textSecondary">
      {t('common.loading')}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <DynamicThemeApplicator />
        <Router>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/auth/landing" element={<Landing />} />
            <Route path="/auth/sso" element={<SsoLogin />} />
            <Route path="/auth/sso/complete" element={<SsoComplete />} />
            <Route path="/auth/saml/error" element={<SamlError />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/verify-email" element={<EmailVerification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/booking/cancel/:token" element={<BookingCancelPage />} />

            {/* Live Tracking - öppnas i nytt fönster, kräver autentisering */}
            <Route
              path="/live-tracking"
              element={
                <ProtectedRoute>
                  <LiveTracking />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DeviceProfileProvider>
                    <Layout />
                  </DeviceProfileProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="fleet" element={<FleetDashboard />} />
              <Route path="fleet/overview" element={<FleetOverview />} />
              <Route
                path="fleet/unplug-events"
                element={
                  <AdminRoute featureCode={['UNPLUG_EVENTS', 'WIDGET_UNPLUGGED']}>
                    <UnplugEventsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="fleet/economy-export"
                element={
                  <AdminRoute featureCode="SYSTEM_ECONOMY">
                    <EconomyExportPage />
                  </AdminRoute>
                }
              />
              <Route
                path="fleet/cost-invoices"
                element={
                  <AdminRoute featureCode="COST_INVOICE_IMPORT">
                    <Suspense fallback={<RouteFallback />}>
                      <CostInvoiceImportPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="fleet/vehicle-economy"
                element={
                  <AdminRoute
                    featureCode={['VEHICLE_COST_ANALYSIS', 'COST_INVOICE_IMPORT', 'SYSTEM_ECONOMY']}
                  >
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleCostAnalysisPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route path="organization" element={<OrganizationDashboard />} />
              <Route path="organization/chart" element={<OrganizationChart />} />
              <Route path="crm" element={<CrmDashboard />} />
              <Route path="crm/customers" element={<CustomerList />} />
              <Route path="crm/customers/new" element={<CustomerWizard />} />
              <Route path="crm/customers/:id" element={<CustomerDetail />} />
              <Route
                path="crm/projects"
                element={
                  <AdminRoute featureCode={['CRM_PROJECTS', 'CRM']}>
                    <CrmProjectsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="crm/projects/:id"
                element={
                  <AdminRoute featureCode={['CRM_PROJECTS', 'CRM']}>
                    <CrmProjectDetailPage />
                  </AdminRoute>
                }
              />
              <Route path="crm/procurements" element={<Navigate to="/crm/pipeline?type=procurement" replace />} />
              <Route
                path="crm/pipeline"
                element={
                  <AdminRoute featureCode={['CRM_PIPELINE', 'CRM']}>
                    <SalesPipelinePage />
                  </AdminRoute>
                }
              />
              <Route
                path="crm/pipeline/new"
                element={
                  <AdminRoute featureCode={['CRM_PIPELINE', 'CRM']}>
                    <PipelineCaseFormPage />
                  </AdminRoute>
                }
              />
              <Route
                path="crm/pipeline/:id"
                element={
                  <AdminRoute featureCode={['CRM_PIPELINE', 'CRM']}>
                    <PipelineCaseDetailPage />
                  </AdminRoute>
                }
              />
              <Route path="crm/economy" element={<ComingSoon title="Ekonomisk översikt" description="Rullande 12, trender och intäkter" />} />
              <Route path="crm/invoices" element={<ComingSoon title="Fakturaunderlag" description="Generera och hantera fakturor" />} />
              <Route path="crm/pricelist" element={<Pricelist />} />
              <Route
                path="crm/module-catalog"
                element={
                  <AdminRoute featureCode={['CRM_MODULE_CATALOG', 'CRM']}>
                    <ModuleCatalogPage />
                  </AdminRoute>
                }
              />
              <Route path="crm/scb-testing" element={<ScbTesting />} />
              <Route path="reports" element={<Navigate to="/system/reports" replace />} />
              <Route path="reports/trips" element={<TripsView />} />
              <Route path="reports/eco-driving" element={<EcoDriving />} />
              <Route path="reports/speed-compliance" element={<SpeedComplianceReport />} />
              <Route path="reports/energy-consumption" element={<EnergyConsumptionReport />} />
              <Route path="reports/vehicle-wear" element={<VehicleWearReport />} />
              <Route path="reports/underutilized-vehicles" element={<UnderutilizedReport />} />
              <Route path="reports/co2" element={<CO2Report />} />
              <Route
                path="reports/booking-stats"
                element={
                  <AdminRoute featureCode="REPORTS_BOOKING_STATS">
                    <BookingStatsReport />
                  </AdminRoute>
                }
              />
              <Route path="reports/geofence-zones" element={<GeofenceZoneReport />} />
              <Route
                path="reports/vehicle-kpi"
                element={
                  <AdminRoute featureCode="REPORTS_VEHICLE_KPI">
                    <VehicleKpiReport />
                  </AdminRoute>
                }
              />
              <Route
                path="reports/dtc"
                element={
                  <AdminRoute featureCode="REPORTS_DTC">
                    <DtcReport />
                  </AdminRoute>
                }
              />
              <Route
                path="reports/rental"
                element={
                  <AdminRoute featureCode="REPORT_RENTAL">
                    <RentalReport />
                  </AdminRoute>
                }
              />
              <Route path="reports/driving-log" element={<DrivingLog />} />
              <Route path="reports/odometer" element={<OdometerReport />} />
              <Route
                path="reports/service-costs"
                element={
                  <ServiceCostsReportGate>
                    <Suspense fallback={<RouteFallback />}>
                      <ServiceCostsReport />
                    </Suspense>
                  </ServiceCostsReportGate>
                }
              />
              <Route
                path="reports/fuel-analysis"
                element={
                  <AdminRoute featureCode="REPORTS_FUEL_ANALYSIS">
                    <Suspense fallback={<RouteFallback />}>
                      <FuelAnalysisReport />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="reports/fleet-tco"
                element={
                  <AdminRoute featureCode="REPORTS_FLEET_TCO">
                    <Suspense fallback={<RouteFallback />}>
                      <FleetTcoReport />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="reports/executive"
                element={
                  <AdminRoute featureCode="REPORTS_EXECUTIVE">
                    <Suspense fallback={<RouteFallback />}>
                      <ExecutiveDashboard />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route path="analysis" element={<Navigate to="/" replace />} />
              <Route path="analysis/heatmap" element={<Heatmap />} />
              <Route path="analysis/heatmap-import" element={<HeatmapImport />} />
              <Route path="analysis/stop-time" element={<StopTimeAnalysis />} />
              <Route path="analysis/ai" element={<AIAnalysis />} />
              <Route path="analysis/route" element={<RouteAnalysis />} />
              <Route
                path="analysis/tires"
                element={
                  <AdminRoute featureCode="ANALYSIS_TIRES">
                    <TireAnalysis />
                  </AdminRoute>
                }
              />
              <Route
                path="analysis/statistics"
                element={
                  <AdminRoute featureCode="REPORTS_EXECUTIVE">
                    <Suspense fallback={<RouteFallback />}>
                      <ExecutiveDashboard />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="analysis/trends"
                element={
                  <AdminRoute featureCode={['ANALYSIS_TRENDS', 'REPORTS_EXECUTIVE']}>
                    <Suspense fallback={<RouteFallback />}>
                      <TrendAnalysis />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="analysis/benchmarks"
                element={
                  <AdminRoute featureCode={['ANALYSIS_BENCHMARKS', 'REPORTS_EXECUTIVE']}>
                    <Suspense fallback={<RouteFallback />}>
                      <BenchmarkAnalysis />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="analysis/crash-detection"
                element={
                  <AdminRoute featureCode="ANALYSIS_CRASH_DETECTION">
                    <CrashDetection />
                  </AdminRoute>
                }
              />
              {/* Work Orders - moduler (separata RBAC-features) */}
              <Route path="work-orders/craftsman" element={
                <AdminRoute featureCode="WORK_ORDERS"><CraftsmanPage /></AdminRoute>
              } />
              <Route path="work-orders/delivery" element={
                <AdminRoute featureCode="WORK_ORDERS_DELIVERY"><DeliveryPage /></AdminRoute>
              } />
              <Route path="work-orders/homecare" element={
                <AdminRoute featureCode="WORK_ORDERS_HOMECARE"><HomecarePage /></AdminRoute>
              } />
              <Route path="work-orders/service-agreements" element={
                <AdminRoute featureCode="SERVICE_AGREEMENTS"><ServiceAgreementsPage /></AdminRoute>
              } />
              <Route
                path="service"
                element={
                  <AdminRoute featureCode="SYSTEM_SERVICE">
                    <Suspense fallback={<RouteFallback />}>
                      <ServiceOverview />
                    </Suspense>
                  </AdminRoute>
                }
              />
              {/* Booking: kalender, utlämningsplatser, leverantörer, destinationer */}
              <Route
                path="booking"
                element={
                  <AdminRoute featureCode="BOOKING_CALENDAR">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingCalendarPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/calendar"
                element={
                  <AdminRoute featureCode="BOOKING_CALENDAR">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingCalendarPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/requests"
                element={
                  <AdminRoute featureCode="BOOKING_REQUESTS">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingRequestsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/bokningsunderlag"
                element={
                  <AdminRoute featureCode="BOOKING_BASIS">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingBasisPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/bookings-search"
                element={
                  <AdminRoute featureCode="BOOKING_SEARCH">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingSearchPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/optimization"
                element={
                  <AdminRoute featureCode="BOOKING_OPTIMIZATION">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingOptimizationPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/resources"
                element={
                  <AdminRoute featureCode="BOOKING_RESOURCES">
                    <Suspense fallback={<RouteFallback />}>
                      <BookingResourcesPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/resource-types"
                element={<Navigate to="/booking/resources" replace />}
              />
              <Route
                path="booking/pickup-locations"
                element={
                  <AdminRoute featureCode="BOOKING_PICKUP_LOCATIONS">
                    <Suspense fallback={<RouteFallback />}>
                      <PickupLocationsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/suppliers"
                element={
                  <AdminRoute featureCode="BOOKING_SUPPLIERS">
                    <Suspense fallback={<RouteFallback />}>
                      <SuppliersPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/rental-management"
                element={
                  <AdminRoute featureCode="BOOKING_RENTAL_LINKS">
                    <Suspense fallback={<RouteFallback />}>
                      <RentalManagementPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/destinations"
                element={<Navigate to="/booking/settings?tab=destinations" replace />}
              />
              <Route
                path="booking/cost-centers"
                element={<Navigate to="/booking/settings?tab=costCenters" replace />}
              />
              <Route
                path="booking/settings"
                element={
                  <AdminRoute featureCode={[...BOOKING_SETTINGS_ROUTE_FEATURES]}>
                    <Suspense fallback={<RouteFallback />}>
                      <BookingSettingsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/resource-manager"
                element={
                  <AdminRoute featureCode="BOOKING_RESOURCE_MANAGER">
                    <Suspense fallback={<RouteFallback />}>
                      <ResourceManagerPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/old-booking"
                element={
                  <OldBookingRoute>
                    <Suspense fallback={<RouteFallback />}>
                      <OldBookingPage />
                    </Suspense>
                  </OldBookingRoute>
                }
              />
              <Route
                path="booking/collision-bookings"
                element={
                  <AdminRoute featureCode="BOOKING_COLLISION_BOOKINGS">
                    <Suspense fallback={<RouteFallback />}>
                      <CollisionBookingsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/vehicle-replacement"
                element={
                  <AdminRoute featureCode="BOOKING_VEHICLE_REPLACEMENT">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleReplacementPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/travel-planner"
                element={
                  <AdminRoute featureCode="BOOKING_TRAVEL_PLANNER">
                    <Suspense fallback={<RouteFallback />}>
                      <TravelPlannerPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/own-car-trip"
                element={
                  <AdminRoute featureCode={['BOOKING_OWN_CAR_TRIP', 'BOOKING_OWN_CAR_TRIPS']}>
                    <Suspense fallback={<RouteFallback />}>
                      <OwnCarTripFormPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/own-car-trips"
                element={
                  <AdminRoute featureCode="BOOKING_OWN_CAR_TRIPS">
                    <Suspense fallback={<RouteFallback />}>
                      <OwnCarTripsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/purposes"
                element={<Navigate to="/booking/settings?tab=tripPurposes" replace />}
              />
              <Route
                path="booking/terms"
                element={<Navigate to="/booking/settings?tab=terms" replace />}
              />
              <Route
                path="booking/vehicle-pools"
                element={
                  <AdminRoute featureCode="BOOKING_VEHICLE_POOL">
                    <Suspense fallback={<RouteFallback />}>
                      <VehiclePoolPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/pool-watch"
                element={
                  <AdminRoute featureCode={['BOOKING_VEHICLE_POOL', 'BOOKING_SEARCH', 'BOOKING_CALENDAR']}>
                    <Suspense fallback={<RouteFallback />}>
                      <BookingPoolWatchPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="booking/equipment-tags"
                element={
                  <AdminRoute featureCode="BOOKING_EQUIPMENT_TAGS">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleEquipmentTagsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              {/* Digital Tidbok */}
              <Route
                path="tidbok"
                element={
                  <AdminRoute featureCode="TIDBOK_DIGITAL">
                    <Suspense fallback={<RouteFallback />}>
                      <TidbokPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              {/* Demo: Poolbil-simulering för Region Skåne */}
              <Route
                path="demo/poolcar"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <PoolCarDemo />
                  </Suspense>
                }
              />
              <Route path="statistics" element={<Reports />} />
              <Route path="profile" element={<Reports />} />
              <Route path="manual" element={<UserManual />} />
              <Route path="testing" element={<TestRunner />} />
              
              {/* Deque system routes - MÅSTE komma FÖRE dynamiska system/:systemName routen */}
              <Route path="system/deque/map-services" element={<MapServicesDashboard />} />
              <Route path="system/deque/track" element={<TrackPlayback />} />
              <Route path="system/deque/fuel-management" element={<FuelManagement />} />
              <Route
                path="system/deque/fuel-cards"
                element={
                  <AdminRoute featureCode="DEQUE_FUEL_CARDS">
                    <Suspense fallback={<RouteFallback />}>
                      <FuelCards />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route path="system/deque/assignments" element={<Assignments />} />
              <Route
                path="system/deque/vehicle-log"
                element={
                  <AdminRoute featureCode="DEQUE_VEHICLE_LOG">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleLog />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/replacement-chain"
                element={
                  <AdminRoute featureCode="DEQUE_REPLACEMENT_CHAIN">
                    <Suspense fallback={<RouteFallback />}>
                      <ReplacementChainPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/vehicle-categories"
                element={
                  <AdminRoute featureCode="DEQUE_VEHICLE_CATEGORIES">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleCategories />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/charging"
                element={
                  <AdminRoute featureCode="DEQUE_CHARGING">
                    <Suspense fallback={<RouteFallback />}>
                      <Charging />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/alternative-fuels"
                element={
                  <AdminRoute featureCode="DEQUE_ALTERNATIVE_FUELS">
                    <Suspense fallback={<RouteFallback />}>
                      <AlternativeFuels />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/usage-statuses"
                element={
                  <AdminRoute featureCode="DEQUE_VEHICLE_USAGE_STATUSES">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleUsageStatuses />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/nordea-leasing"
                element={
                  <AdminRoute featureCode="DEQUE_NORDEA_LEASING">
                    <Suspense fallback={<RouteFallback />}>
                      <NordeaLeasingImport />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="system/deque/protector-insurance"
                element={
                  <AdminRoute featureCode="DEQUE_PROTECTOR_INSURANCE">
                    <Suspense fallback={<RouteFallback />}>
                      <ProtectorInsuranceImport />
                    </Suspense>
                  </AdminRoute>
                }
              />

              {/* Nyckelhantering (OnPrem) */}
              <Route
                path="system/keymanagement/manage"
                element={
                  <AdminRoute featureCode="SYSTEM_KEY_MANAGEMENT">
                    <Suspense fallback={<RouteFallback />}>
                      <KeyManagement />
                    </Suspense>
                  </AdminRoute>
                }
              />

              {/* System landing pages - visar moduler för varje system */}
              <Route path="system/:systemName" element={<SystemLandingPage />} />
              
              {/* Redirect legacy system routes to new format */}
              <Route path="deque" element={<Navigate to="/system/deque" replace />} />

              {/* Admin routes - styrs av RBAC (is_global_admin har alltid tillgång) */}
              <Route
                path="admin"
                element={<Navigate to="/system/administration" replace />}
              />
              <Route
                path="admin/roles"
                element={
                  <AdminRoute featureCode="ADMIN_ROLES">
                    <RolesManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/organizations"
                element={<OrganizationsManagement />}
              />
              <Route
                path="admin/users"
                element={
                  <AdminRoute featureCode="ADMIN_USERS">
                    <UsersManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/dev-projects"
                element={
                  <AdminRoute featureCode="DEV_PROJECTS">
                    <DevProjectsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/dev-projects/:id"
                element={
                  <AdminRoute featureCode="DEV_PROJECTS">
                    <DevProjectDetailPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/translations"
                element={
                  <AdminRoute featureCode="ADMIN_TRANSLATIONS">
                    <TranslationsManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/imports"
                element={
                  <AdminRoute featureCode="ADMIN_IMPORTS">
                    <ImportsManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/packages"
                element={
                  <AdminRoute featureCode="ADMIN_PACKAGES">
                    <PackagesManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/devices"
                element={
                  <AdminRoute featureCode="ADMIN_DEVICES">
                    <DevicesManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/device-management"
                element={
                  <AdminRoute featureCode="ADMIN_DEVICE_MANAGEMENT">
                    <DeviceManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/device-avl-capabilities"
                element={
                  <AdminRoute featureCode="ADMIN_DEVICE_MANAGEMENT">
                    <DeviceAvlCapabilities />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/obd-vehicles"
                element={
                  <AdminRoute featureCode="ADMIN_OBD_VEHICLES">
                    <ObdVehicleListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/obd-knowledge"
                element={
                  <AdminRoute featureCode="ADMIN_OBD_KNOWLEDGE">
                    <ObdKnowledgePage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sim-cards"
                element={
                  <AdminRoute featureCode="ADMIN_SIM_CARDS">
                    <SimCardManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/geofences"
                element={
                  <AdminRoute featureCode="ADMIN_GEOFENCES">
                    <Geofences />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/geofence-events"
                element={
                  <AdminRoute featureCode="ADMIN_GEOFENCE_EVENTS">
                    <GeofenceEvents />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/jam-detection"
                element={
                  <AdminRoute featureCode="ADMIN_JAM_DETECTION">
                    <JamDetection />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/support"
                element={
                  <AdminRoute featureCode="ADMIN_SUPPORT">
                    <SupportAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/damage-reports"
                element={
                  <AdminRoute featureCode="ADMIN_DAMAGE_REPORTS">
                    <DamageReports />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/carinfo-org-sync"
                element={
                  <AdminRoute featureCode="ADMIN_CARINFO_ORG_SYNC">
                    <CarinfoOrgSync />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/flinkey-boxes"
                element={
                  <AdminRoute featureCode="ADMIN_FLINKEY">
                    <FlinkeyBoxesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/guide-management"
                element={
                  <AdminRoute featureCode="ADMIN_GUIDE">
                    <GuideManagement />
                  </AdminRoute>
                }
              />
              {/* SysAdmin route - styrs av RBAC */}
              <Route
                path="admin/sysadmin"
                element={
                  <AdminRoute featureCode="SYSADMIN">
                    <SysAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/roles"
                element={
                  <AdminRoute featureCode="SYSADMIN">
                    <RolesManagement mode="system" />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/data-migration"
                element={
                  <AdminRoute featureCode="SYSADMIN_DATA_MIGRATION">
                    <DataMigration />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/device-commands"
                element={
                  <AdminRoute featureCode="SYSADMIN_DEVICE_COMMANDS">
                    <DeviceCommandBuilder />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/theme-settings"
                element={
                  <AdminRoute featureCode="ADMIN_THEME_SETTINGS">
                    <ThemeSettingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/trip-analysis"
                element={
                  <AdminRoute featureCode="ADMIN_TRIP_ANALYSIS">
                    <TripAnalysisPage />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/overview"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING">
                    <TemperatureMonitoringOverview />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/sensors"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING">
                    <TemperatureMonitoringSensors />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/config"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING" requiredAction="CONFIGURE_DEVICE">
                    <TemperatureMonitoringConfig />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/recipients"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING">
                    <TemperatureMonitoringRecipients />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/schedules"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING">
                    <TemperatureMonitoringSchedules />
                  </AdminRoute>
                }
              />
              <Route
                path="temperature-monitoring/reports"
                element={
                  <AdminRoute featureCode="TEMPERATURE_MONITORING">
                    <TemperatureMonitoringReports />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/fota-migration"
                element={
                  <AdminRoute featureCode="ADMIN_FOTA_MIGRATION">
                    <FotaWebMigration />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/audit-log"
                element={
                  <AdminRoute featureCode="SYSADMIN_AUDIT_LOG">
                    <AuditLog />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/general-audit-log"
                element={
                  <AdminRoute featureCode="SYSADMIN_GENERAL_AUDIT_LOG">
                    <GeneralAuditLog />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/error-log"
                element={
                  <AdminRoute featureCode="SYSADMIN_ERROR_LOG">
                    <ErrorLog />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/carinfo-filter-config"
                element={
                  <AdminRoute featureCode="SYSADMIN_CARINFO_FILTER">
                    <CarInfoFilterConfig />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/ai-log"
                element={
                  <AdminRoute featureCode="SYSADMIN_AI_LOG">
                    <AILog />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/eco-settings"
                element={
                  <AdminRoute featureCode="SYSADMIN_ECO_SETTINGS">
                    <EcoSettings />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/system-monitor"
                element={
                  <AdminRoute featureCode="SYSADMIN_SYSTEM_MONITOR">
                    <SystemMonitor />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/slow-queries"
                element={
                  <AdminRoute featureCode="SYSADMIN_SLOW_QUERIES">
                    <SlowQueryMonitor />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/certificate-monitor"
                element={
                  <AdminRoute featureCode="SYSADMIN_CERT_MONITOR">
                    <CertificateMonitor />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/security"
                element={
                  <AdminRoute requireSysAdminRole>
                    <SecurityPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/sso-log"
                element={
                  <AdminRoute requireSysAdminRole>
                    <SsoLogPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/gps-data-lag"
                element={
                  <AdminRoute requireSysAdminRole>
                    <GpsDataLagPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/device-queues"
                element={
                  <AdminRoute featureCode="SYSADMIN_DEVICE_QUEUES">
                    <DeviceQueueMonitor />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/data-replay"
                element={
                  <AdminRoute featureCode="SYSADMIN_DATA_REPLAY">
                    <DataReplay />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/import-sources"
                element={
                  <AdminRoute featureCode="SYSADMIN">
                    <ImportSourcesManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/trip-validation"
                element={
                  <AdminRoute featureCode="SYSADMIN_TRIP_VALIDATION">
                    <TripValidation />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/trip-sync"
                element={
                  <AdminRoute featureCode="SYSADMIN_TRIP_VALIDATION">
                    <TripSyncPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/sysadmin/vehicle-carinfo-sync"
                element={
                  <AdminRoute featureCode="SYSADMIN_DATA_MIGRATION">
                    <Suspense fallback={<RouteFallback />}>
                      <VehicleCarInfoSync />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </Suspense>
        </Router>
        <Toaster position="top-right" />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
